import React, { useEffect, useState } from "react";
import { Row, Col, Statistic, Typography, Tag, List, Flex, Empty, Spin, Skeleton, Tooltip, Space, Button, Avatar } from "antd";
import {
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  BellOutlined,
  InfoCircleOutlined,
  MailOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import CollapsibleInsightCard from "../CollapsibleInsightCard";
import BottleneckMiniChart, { BottleneckItem } from "../BottleneckMiniChart";
import { JobDescriptionEntity } from "../../../../../../../Entities/JobDescription";
import { InterviewRound, InterviewRoundType } from "../../../../../../../Dto/GenerateJobDescription.dto";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import jobApplicationService from "../../../../../../../Service/job.application.service";
import "./index.scss";

const { Text } = Typography;

// Map chart colors for donut segments
const DONUT_COLORS = {
  qualified: CHART_COLORS.success,
  unqualified: CHART_COLORS.error,
  pending: CHART_COLORS.warning,
};

// Summary data from API
interface SummaryData {
  candidatesStarted: number;
  candidatesQualified: number;
  candidatesUnqualified: number;
  candidatesPending: number;
  manuallyAdvancedCount: number;
}

// Round stat interface (combines config with API data)
interface RoundStat {
  roundId: string;
  roundNumber: number;
  roundName: string;
  isMustQualify: boolean;
  passPercentage: number;
  stats: {
    totalStarted: number;
    qualified: number;
    unqualified: number;
    pending: number;
    successRate: number;
  };
}

// API response type
interface RoundApiData {
  roundNumber: number;
  roundId: string;
  roundName: string;
  totalStarted: number;
  qualified: number;
  unqualified: number;
  pending: number;
  successRate: number;
}

// Assessment status filter type (for navigation)
export type AssessmentStatusFilter = "all" | "qualified" | "unqualified" | "pending";

interface AssessmentsInsightCardProps {
  jobPost: JobDescriptionEntity;
  interviewRoundDetails: InterviewRound[];
  themeColor?: string;
  /** Called when a round is clicked in expanded view */
  onRoundClick?: (roundId: string, status: "qualified" | "unqualified" | "pending") => void;
  /** Called when a stat card is clicked - navigates to applications with assessment filter */
  onAssessmentStatusClick?: (status: AssessmentStatusFilter) => void;
  /** Controlled mode: external expanded state */
  expanded?: boolean;
  /** Controlled mode: callback when expand/collapse is toggled */
  onExpandChange?: (expanded: boolean) => void;
}

// Helper function to format time ago
const getTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
  } else if (diffHours < 24) {
    const mins = diffMins % 60;
    return mins > 0 
      ? `${diffHours}h ${mins}m ago`
      : `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  }
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Skeleton loader for stat cards
const StatCardSkeleton: React.FC = () => (
  <div className="stat-box">
    <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 8 }} />
    <Skeleton.Input active size="large" style={{ width: 40 }} />
  </div>
);

// Skeleton loader for chart area
const ChartSkeleton: React.FC = () => (
  <div className="chart-skeleton">
    <Skeleton.Input active size="small" style={{ width: 180, marginBottom: 12 }} />
    <Skeleton active paragraph={{ rows: 3, width: ['100%', '80%', '60%'] }} title={false} />
  </div>
);

// Skeleton loader for round item
const RoundItemSkeleton: React.FC = () => (
  <List.Item className="round-item">
    <Flex align="center" gap={16} style={{ width: "100%" }}>
      <Skeleton.Avatar active size={80} shape="circle" />
      <Flex vertical style={{ flex: 1 }} gap={8}>
        <Flex gap={8}>
          <Skeleton.Button active size="small" style={{ width: 60 }} />
          <Skeleton.Input active size="small" style={{ width: 120 }} />
        </Flex>
        <Flex gap={16}>
          <Skeleton.Button active size="small" style={{ width: 80 }} />
          <Skeleton.Button active size="small" style={{ width: 80 }} />
          <Skeleton.Button active size="small" style={{ width: 80 }} />
        </Flex>
      </Flex>
    </Flex>
  </List.Item>
);

const AssessmentsInsightCard: React.FC<AssessmentsInsightCardProps> = ({
  jobPost,
  interviewRoundDetails,
  themeColor,
  onRoundClick,
  onAssessmentStatusClick,
  expanded,
  onExpandChange,
}) => {
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [apiData, setApiData] = useState<RoundApiData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [, setForceUpdate] = useState(0); // For re-rendering time ago

  // Fetch assessment stats from API
  const fetchData = async (isRefresh = false) => {
    if (!jobPost?._id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await jobApplicationService.getInterviewRoundSuccessRates(jobPost._id);
      setSummaryData(response.summary || null);
      setApiData(response.rounds || []);
      setLastSynced(new Date());
      setHasFetchedOnce(true);
    } catch (err) {
      console.error("Failed to fetch assessment stats:", err);
      setError("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [jobPost?._id]);

  // Update "time ago" display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastSynced) {
        setForceUpdate((prev) => prev + 1);
      }
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastSynced]);

  // Handle refresh
  const handleRefresh = () => {
    fetchData(true);
  };

  const hasRounds = jobPost?.interviewRounds && jobPost.interviewRounds.length > 0;

  // Generate subtitle with last synced time
  const subtitle = lastSynced ? `Last synced: ${getTimeAgo(lastSynced)}` : undefined;

  if (!hasRounds) {
    return (
      <CollapsibleInsightCard
        title="Assessments"
        subtitle={subtitle}
        icon={<ExperimentOutlined />}
        onRefresh={handleRefresh}
        isRefreshing={loading}
        themeColor={themeColor}
        expanded={expanded}
        onExpandChange={onExpandChange}
      >
        <Empty
         image={Empty.PRESENTED_IMAGE_SIMPLE}
         // description="No assessment rounds configured"
        />
      </CollapsibleInsightCard>
    );
  }

  // Determine if we're in initial load or refresh state
  const isInitialLoad = loading && !hasFetchedOnce;
  const isRefreshing = loading && hasFetchedOnce;

  // Build round stats by combining job config with API data
  // Exclude "profile-match" — it is shown separately in the Profile Screening card
  const rounds = jobPost?.interviewRounds || [];
  const allRoundStats: RoundStat[] = rounds
    .filter((round: InterviewRoundType) => round.interviewRoundId && round.interviewRoundId !== "profile-match")
    .map((round: InterviewRoundType, index: number) => {
      // Get round config from job post
      const roundDetail = interviewRoundDetails?.find(
        (r) => r._id === round.interviewRoundId
      );

      // Get API stats for this round by roundId (stable identifier)
      const apiStats = apiData.find((a) => a.roundId === round.interviewRoundId);

      return {
        roundId: round.interviewRoundId as string,
        roundNumber: index + 1,
        roundName: roundDetail?.title || apiStats?.roundName || `Round ${index + 1}`,
        isMustQualify: round.isMustQualify,
        passPercentage: round.passPercentage ?? 0,
        stats: {
          totalStarted: apiStats?.totalStarted || 0,
          qualified: apiStats?.qualified || 0,
          unqualified: apiStats?.unqualified || 0,
          pending: apiStats?.pending || 0,
          successRate: apiStats?.successRate || 0,
        },
      };
    });

  // Check if we have any stats data
  const hasStatsData = summaryData && summaryData.candidatesStarted > 0;
  const totalStarted = summaryData?.candidatesStarted || 0;
  const qualifiedCount = summaryData?.candidatesQualified || 0;
  const unqualifiedCount = summaryData?.candidatesUnqualified || 0;
  const pendingCount = summaryData?.candidatesPending || 0;
  const manuallyAdvancedCount = summaryData?.manuallyAdvancedCount || 0;

  // Calculate total completed (qualified + unqualified) for pass rate
  const totalCompleted = qualifiedCount + unqualifiedCount;
  const passRate = totalCompleted > 0 ? Math.round((qualifiedCount / totalCompleted) * 100) : 0;

  // Data for semi-circle chart
  const summaryChartData = [
    { name: "Qualified", value: qualifiedCount, color: CHART_COLORS.success },
    { name: "Unqualified", value: unqualifiedCount, color: CHART_COLORS.error },
  ];

  // Get bottlenecks - rounds sorted by FAILURE COUNT (highest first = biggest bottleneck)
  const bottlenecks = [...allRoundStats]
    .filter((r) => r.stats.unqualified > 0)
    .sort((a, b) => b.stats.unqualified - a.stats.unqualified)
    .slice(0, 3);

  // Convert bottlenecks to chart items - showing failures out of TOTAL CANDIDATES STARTED
  const bottleneckItems: BottleneckItem[] = hasStatsData
    ? bottlenecks.map((b) => {
        // Calculate failure rate against total candidates who started assessments
        const failureRate = totalStarted > 0
          ? Math.round((b.stats.unqualified / totalStarted) * 100)
          : 0;
        return {
          id: b.roundId,
          label: b.roundName.length > 30 ? b.roundName.substring(0, 30) + "..." : b.roundName,
          rate: failureRate,
          count: {
            success: b.stats.unqualified, // Number of candidates who failed this round
            total: totalStarted, // Total candidates who started assessments
          },
          isCritical: failureRate >= 50,
        };
      })
    : [];

  const handleBottleneckClick = (item: BottleneckItem) => {
    onRoundClick?.(item.id as string, "unqualified");
  };

  const handleStatClick = (status: AssessmentStatusFilter) => {
    onAssessmentStatusClick?.(status);
  };

  // Summary content (collapsed view) - Semi-circle chart with pass rate
  const summaryContent = (
    <div className="assessments-summary">
      {isInitialLoad ? (
        <Flex justify="center" align="center" style={{ minHeight: 100 }}>
          <Spin size="small" />
        </Flex>
      ) : error ? (
        <Text type="danger">{error}</Text>
      ) : (
        <>
          {/* Semi-circle Chart + Stats */}
          {hasStatsData ? (
            <Flex align="center" justify="center" gap={32} className="pass-rate-section">
              {/* Semi-circle Pie Chart */}
              <Tooltip
                title={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Assessment Results Distribution</div>
                    <div style={{ color: CHART_COLORS.success }}>● Qualified: {qualifiedCount} candidates</div>
                    <div style={{ color: CHART_COLORS.error }}>● Unqualified: {unqualifiedCount} candidates</div>
                    {pendingCount > 0 && <div style={{ color: CHART_COLORS.warning }}>● In Progress: {pendingCount} candidates</div>}
                  </div>
                }
                placement="top"
              >
                <div className="semi-circle-chart-container">
                  <ResponsiveContainer width={160} height={90}>
                    <PieChart>
                      <Pie
                        data={summaryChartData.length > 0 && totalCompleted > 0 
                          ? summaryChartData 
                          : [{ name: "No Data", value: 1, color: "#e8e8e8" }]}
                          cx="50%"
                          cy="100%"
                          startAngle={180}
                          endAngle={0}
                          innerRadius={50}
                          outerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {(summaryChartData.length > 0 && totalCompleted > 0 
                          ? summaryChartData 
                          : [{ color: "#e8e8e8" }]).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <Tooltip
                    title={`${passRate}% of candidates who completed assessments met the qualifying criteria`}
                    placement="bottom"
                  >
                    <div className="chart-center-value">
                      <Text strong style={{ fontSize: 24, color: CHART_COLORS.success, cursor: "help" }}>
                        {passRate}%
                      </Text>
                    </div>
                  </Tooltip>
                </div>
              </Tooltip>

              {/* Pass Rate Stats */}
              <Flex vertical gap={8} className="pass-rate-stats" align="flex-start" justify="flex-start">
                <Tooltip
                  title={`${qualifiedCount} candidate${qualifiedCount !== 1 ? 's' : ''} scored above the pass percentage threshold in all required assessments`}
                  placement="right"
                >
                  <Text style={{ fontSize: 14, fontWeight: 400, cursor: "help" }}>
                    <CheckCircleOutlined style={{ color: CHART_COLORS.success, marginRight: 8 }} />
                    {qualifiedCount} of {totalCompleted} qualified
                  </Text>
                </Tooltip>
                {unqualifiedCount > 0 && (
                  <Tooltip
                    title={
                      <div>
                        <div>{unqualifiedCount} candidate{unqualifiedCount !== 1 ? 's' : ''} did not meet the pass percentage in one or more assessments</div>
                        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view details</div>
                      </div>
                    }
                    placement="right"
                  >
                    <Text 
                      className="stat-link" 
                      style={{ fontSize: 14, fontWeight: 400, padding: 0 }}
                      onClick={() => handleStatClick("unqualified")}
                    >
                      <CloseCircleOutlined style={{ color: CHART_COLORS.error, marginRight: 8 }} />
                      {unqualifiedCount} did not qualify
                    </Text>
                  </Tooltip>
                )}
                
                <Tooltip
                  title="Combined results from all AI-powered assessments configured for this job post"
                  placement="right"
                >
                  <Text type="secondary" style={{ fontSize: 14, cursor: "help" }}>
                    Across All Assessments
                  </Text>
                </Tooltip>
              </Flex>
            </Flex>
          ) : (
            <Empty 
             image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Assessment data will appear once candidates complete assessments" 
              style={{ margin: "16px 0" }}
            />
          )}

          {/* Pending Assessments CTA */}
          {pendingCount > 0 && (
            <div className="pending-assessments-cta">
              <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
                <Flex align="center" gap={8}>
                  <ClockCircleOutlined style={{ color: CHART_COLORS.warning, fontSize: 16 }} />
                  <Text type="secondary">
                    <strong style={{ color: CHART_COLORS.warning }}>{pendingCount}</strong> candidate{pendingCount > 1 ? 's' : ''} have assessments in progress
                  </Text>
                </Flex>
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleStatClick("pending")}
                >
                  View 
                </Button>
              </Flex>
            </div>
          )}

          {/* Manually Advanced Info Callout */}
          {manuallyAdvancedCount > 0 && (
            <div className="manually-advanced-info">
              <Flex align="center" gap={8}>
                <InfoCircleOutlined style={{ color: CHART_COLORS.info, fontSize: 16 }} />
                <Text type="secondary">
                  <strong style={{ color: CHART_COLORS.info }}>{manuallyAdvancedCount}</strong> candidate{manuallyAdvancedCount > 1 ? 's were' : ' was'} manually advanced despite failing profile screening
                </Text>
              </Flex>
            </div>
          )}

{/* Empty state is now handled in the pass-rate-section conditional above */}

          {/* Commented out: Previous Stats Row and Bottleneck Chart
          <Row gutter={[12, 12]} className="stats-row">
            <Col xs={12} sm={6}>
              {isRefreshing ? (
                <StatCardSkeleton />
              ) : (
                <Tooltip title="View all candidates who started assessments">
                  <div
                    className="stat-box clickable"
                    onClick={() => handleStatClick("all")}
                  >
                    <Statistic
                      title="Started"
                      value={summaryData?.candidatesStarted || 0}
                      valueStyle={{ fontSize: 20, fontWeight: 600 }}
                    />
                  </div>
                </Tooltip>
              )}
            </Col>
            ...
          </Row>

          {isRefreshing ? (
            <div className="bottlenecks-section">
              <ChartSkeleton />
            </div>
          ) : hasStatsData && bottleneckItems.length > 0 ? (
            <div className="bottlenecks-section">
              <BottleneckMiniChart
                title="Rounds failing most candidates"
                items={bottleneckItems}
                onItemClick={handleBottleneckClick}
                themeColor={themeColor}
                mode="failure"
                rateLabel="failure rate"
                countLabel="failed"
                totalEvaluated={totalStarted}
              />
            </div>
          ) : null}
          */}
        </>
      )}
    </div>
  );

  // Mini Donut Chart component for each round
  const MiniDonutChart: React.FC<{ stats: RoundStat["stats"]; isEmpty?: boolean }> = ({ stats, isEmpty }) => {
    const total = stats.totalStarted;

    // If no data, show grey placeholder
    if (isEmpty || total === 0) {
      const placeholderData = [{ name: "No Data", value: 1, color: "#e8e8e8" }];
      return (
        <div className="mini-donut-container placeholder">
          <ResponsiveContainer width={80} height={80}>
            <PieChart>
              <Pie
                data={placeholderData}
                cx="50%"
                cy="50%"
                innerRadius={32}
                outerRadius={40}
                dataKey="value"
                strokeWidth={0}
              >
                <Cell fill="#e8e8e8" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="donut-center-label">
            <span className="total" style={{ color: "#bfbfbf" }}>0</span>
            <span className="label">attempted</span>
          </div>
        </div>
      );
    }

    const donutData = [
      { name: "Qualified", value: stats.qualified, color: DONUT_COLORS.qualified },
      { name: "Unqualified", value: stats.unqualified, color: DONUT_COLORS.unqualified },
      { name: "In Progress", value: stats.pending, color: DONUT_COLORS.pending },
    ].filter((d) => d.value > 0);

    return (
      <div className="mini-donut-container">
        <ResponsiveContainer width={80} height={80}>
          <PieChart>
            <Pie
              data={donutData}
              cx="50%"
              cy="50%"
              innerRadius={32}
              outerRadius={40}
              paddingAngle={2}
              dataKey="value"
              strokeWidth={0}
            >
              {donutData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(value: number, name: string) => [
                `${value} (${Math.round((value / total) * 100)}%)`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="donut-center-label">
          <span className="total">{total}</span>
          <small className="label">attempted</small>
        </div>
      </div>
    );
  };

  // Expanded content (detailed view)
  const expandedContent = isInitialLoad ? null : (
    <div className="assessments-details">
      {isRefreshing ? (
        // Show skeleton loaders during refresh
        <>
          <RoundItemSkeleton />
          <RoundItemSkeleton />
        </>
      ) : (
        <List
          size="small"
          dataSource={allRoundStats}
          renderItem={(item, index) => (
            <List.Item 
              className="round-item clickable"
              onClick={() => onRoundClick?.(item.roundId, "qualified")}
            >
              <Flex vertical style={{ width: "100%" }} gap={8}>
                {/* Round Title with Status Icon */}
                <Flex align="center" gap={8} className="round-title-row">
                  <Tooltip 
                    title={item.isMustQualify ? "Eliminator Round" : "Standard Round"}
                  >
                    <Avatar 
                      size={24} 
                      className="round-avatar" 
                      style={{ 
                        cursor: "help",
                        backgroundColor: item.isMustQualify 
                          ? CHART_COLORS.error 
                          : CHART_COLORS.success,
                      }}
                      icon={item.isMustQualify ? <LockOutlined /> : <CheckCircleOutlined />}
                    />
                  </Tooltip>
                  <Tooltip 
                    title={
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.roundName}</div>
                        {item.isMustQualify && <div style={{ color: CHART_COLORS.error }}>● Must qualify to proceed</div>}
                        {item.passPercentage > 0 && <div>● Pass threshold: {item.passPercentage}%</div>}
                        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                      </div>
                    }
                    placement="top"
                  >
                    <Text className="round-title" style={{ cursor: "help" }}>
                      {item.roundName}
                    </Text>
                  </Tooltip>
                </Flex>

                {/* COMMENTED OUT: Mini Donut Chart
                <MiniDonutChart stats={item.stats} isEmpty={item.stats.totalStarted === 0} />
                */}

                {/* COMMENTED OUT: Round Configuration (Must Qualify, Pass %)
                <Flex gap={12} wrap="wrap" className="round-config">
                  {item.isMustQualify && (
                    <Tag color="red" style={{ fontSize: 11 }}>
                      Must Qualify
                    </Tag>
                  )}
                  {item.passPercentage > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Pass: {item.passPercentage}%
                    </Text>
                  )}
                </Flex>
                */}

                {/* Stats - matching Requirements .stat-link typography */}
                {item.stats.totalStarted > 0 ? (
                  <Space size="middle" wrap className="stats-breakdown">
                    <Tooltip 
                      title={
                        <div>
                          <div>{item.stats.qualified} candidate{item.stats.qualified !== 1 ? 's' : ''} scored above {item.passPercentage || 0}% and passed this assessment</div>
                          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                        </div>
                      }
                    >
                      <Text className="stat-item" style={{ cursor: "help" }}>
                        <CheckCircleOutlined style={{ color: CHART_COLORS.success, marginRight: 4 }} />
                        {item.stats.qualified} cleared
                      </Text>
                    </Tooltip>
                    <Tooltip 
                      title={
                        <div>
                          <div>{item.stats.unqualified} candidate{item.stats.unqualified !== 1 ? 's' : ''} scored below {item.passPercentage || 0}% and did not pass this assessment</div>
                          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                        </div>
                      }
                    >
                      <Text className="stat-item" style={{ cursor: "help" }}>
                        <CloseCircleOutlined style={{ color: CHART_COLORS.error, marginRight: 4 }} />
                        {item.stats.unqualified} failed
                      </Text>
                    </Tooltip>
                    <Tooltip 
                      title={
                        <div>
                          <div>{item.stats.pending} candidate{item.stats.pending !== 1 ? 's have' : ' has'} started but not yet completed this assessment</div>
                          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                        </div>
                      }
                    >
                      <Text className="stat-item" style={{ cursor: "help" }}>
                        <ClockCircleOutlined style={{ color: CHART_COLORS.warning, marginRight: 4 }} />
                        {item.stats.pending} in progress
                      </Text>
                    </Tooltip>
                  </Space>
                ) : (
                  <Tooltip title="Candidates will appear here once they start this assessment">
                    <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic", cursor: "help" }}>
                      No assessment data yet
                    </Text>
                  </Tooltip>
                )}
              </Flex>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <CollapsibleInsightCard
      title="Assessments"
      subtitle={subtitle}
      icon={<ExperimentOutlined />}
      onRefresh={handleRefresh}
      isRefreshing={loading}
      themeColor={themeColor}
      expandedContent={expandedContent}
      expanded={expanded}
      onExpandChange={onExpandChange}
    >
      {summaryContent}
    </CollapsibleInsightCard>
  );
};

export default AssessmentsInsightCard;
